import React from 'react';
import {
  Box, Button, Typography, Divider, Tooltip, IconButton,
  ButtonGroup, ToggleButton, ToggleButtonGroup, Autocomplete, TextField
} from '@mui/material';
import { useEditor, EditorContent } from '@tiptap/react';
import { Node } from '@tiptap/core';
import StarterKit from '@tiptap/starter-kit';
import TypographyExtension from '@tiptap/extension-typography';
import Placeholder from '@tiptap/extension-placeholder';
import TextAlign from '@tiptap/extension-text-align';
import Underline from '@tiptap/extension-underline';
import Image from '@tiptap/extension-image';
import Link from '@tiptap/extension-link';
import TextStyle from '@tiptap/extension-text-style';
import { Color } from '@tiptap/extension-color';
import EmojiPicker from './EmojiPicker';

// Icons
import FormatBoldIcon from '@mui/icons-material/FormatBold';
import FormatItalicIcon from '@mui/icons-material/FormatItalic';
import FormatUnderlinedIcon from '@mui/icons-material/FormatUnderlined';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered';
import FormatQuoteIcon from '@mui/icons-material/FormatQuote';
import FormatAlignLeftIcon from '@mui/icons-material/FormatAlignLeft';
import FormatAlignCenterIcon from '@mui/icons-material/FormatAlignCenter';
import FormatAlignRightIcon from '@mui/icons-material/FormatAlignRight';
import LinkIcon from '@mui/icons-material/Link';
import UndoIcon from '@mui/icons-material/Undo';
import RedoIcon from '@mui/icons-material/Redo';
import EmojiSymbolsIcon from '@mui/icons-material/EmojiSymbols';
import EmojiEmotionsIcon from '@mui/icons-material/EmojiEmotions';

const COMMON_SYMBOLS = [
  '➤', '●', '◆', '★', '✓', '✗', '→', '←', '↑', '↓',
  '♥', '♦', '♣', '♠', '©', '®', '™', '€', '$', '£',
  '°', '±', '≤', '≥', '≠', '∞', 'α', 'β', 'γ', 'π'
];

const HEADING_OPTIONS = [
  { label: 'Normal', value: 0 },
  { label: 'Heading 1', value: 1 },
  { label: 'Heading 2', value: 2 },
  { label: 'Heading 3', value: 3 },
];


// Custom Emoji Extension to handle emoji images
const EmojiExtension = Node.create({
  name: 'emoji',
  group: 'inline',
  inline: true,
  atom: true,

  addAttributes() {
    return {
      src: {
        default: null,
      },
      alt: {
        default: null,
      },
      title: {
        default: null,
      },
      'data-emoji-name': {
        default: null,
      },
      class: {
        default: 'emoji-inline',
      },
      style: {
        default: 'height: 1em; width: auto; display: inline; vertical-align: middle; margin: 0 0.1em; object-fit: contain; border: none; background: transparent; font-size: inherit; line-height: 1;',
      },
    };
  },

  parseHTML() {
    return [
      {
        tag: 'img.emoji-inline',
      },
      {
        tag: 'img[data-emoji-name]',
      },
      {
        tag: 'img[alt*="emoji"]',
      },
    ];
  },

  renderHTML({ HTMLAttributes }) {
    return ['img', HTMLAttributes];
  },

  addCommands() {
    return {
      setEmoji: (options) => ({ commands }) => {
        return commands.insertContent({
          type: this.name,
          attrs: options,
        });
      },
    };
  },
});

const EnhancedRichTextEditor = ({ content, onChange, placeholder }) => {
  const [showSymbolPicker, setShowSymbolPicker] = React.useState(false);
  const [showLinkDialog, setShowLinkDialog] = React.useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = React.useState(false);
  const [linkUrl, setLinkUrl] = React.useState('');

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: {
          levels: [1, 2, 3],
        },
      }),
      TypographyExtension,
      TextAlign.configure({
        types: ['heading', 'paragraph'],
      }),
      Underline,
      TextStyle,
      Color,
      EmojiExtension, // Add our custom emoji extension
      Image.configure({
        inline: true,
        allowBase64: true,
        HTMLAttributes: {
          class: 'handbook-image',
        },
        parseHTML() {
          return [
            {
              tag: 'img:not(.emoji-inline):not([data-emoji-name]):not([alt*="emoji"])',
              getAttrs: element => ({
                src: element.getAttribute('src'),
                alt: element.getAttribute('alt'),
                title: element.getAttribute('title'),
                width: element.getAttribute('width'),
                height: element.getAttribute('height'),
                class: element.getAttribute('class') || 'handbook-image',
              }),
            },
          ];
        },
      }),
      Link.configure({
        openOnClick: false,
        HTMLAttributes: {
          class: 'handbook-link',
        },
      }),
      Placeholder.configure({
        placeholder: placeholder || 'Start writing your description...',
      }),
    ],
    content: content || '',
    onUpdate: ({ editor }) => {
      onChange(editor.getHTML());
    },
    editorProps: {
      attributes: {
        class: 'prose prose-sm sm:prose lg:prose-lg xl:prose-2xl mx-auto focus:outline-none',
      },
    },
  });

  // Update editor content when content prop changes (fixes editing issue)
  React.useEffect(() => {
    if (editor && content !== undefined) {
      const currentContent = editor.getHTML();
      // Only update if content is different and not empty
      if (content !== currentContent) {
        console.log('📝 Updating editor content:', { 
          newContent: content, 
          currentContent,
          contentLength: content?.length || 0 
        });
        editor.commands.setContent(content || '<p></p>', false);
      }
    }
  }, [editor, content]);

  // Focus management and event listeners
  React.useEffect(() => {
    if (editor) {
      // Add a small delay to ensure proper initialization
      setTimeout(() => {
        if (content && content.length > 0) {
          console.log('📝 Editor initialized with content:', content.substring(0, 100) + '...');
        }
      }, 100);

    }
  }, [editor]);

  if (!editor) {
    return null;
  }

  const insertSymbol = (symbol) => {
    editor.chain().focus().insertContent(symbol).run();
    setShowSymbolPicker(false);
  };


  const toggleLink = () => {
    if (editor.isActive('link')) {
      editor.chain().focus().unsetLink().run();
    } else {
      setShowLinkDialog(true);
    }
  };

  const setLink = () => {
    if (linkUrl) {
      editor.chain().focus().setLink({ href: linkUrl }).run();
    }
    setShowLinkDialog(false);
    setLinkUrl('');
  };

  const setHeading = (level) => {
    if (level === 0) {
      editor.chain().focus().setParagraph().run();
    } else {
      editor.chain().focus().toggleHeading({ level }).run();
    }
  };

  const getActiveHeading = () => {
    for (let i = 1; i <= 3; i++) {
      if (editor.isActive('heading', { level: i })) {
        return i;
      }
    }
    return 0;
  };



  const Toolbar = () => (
    <Box sx={{ borderBottom: 1, borderColor: 'divider', p: 2, bgcolor: 'grey.50' }}>
      {/* Row 1: Text Formatting */}
      <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        {/* Heading Selector */}
        <Autocomplete
          size="small"
          options={HEADING_OPTIONS}
          getOptionLabel={(option) => option.label}
          value={HEADING_OPTIONS.find(opt => opt.value === getActiveHeading()) || HEADING_OPTIONS[0]}
          onChange={(event, newValue) => setHeading(newValue?.value || 0)}
          renderInput={(params) => (
            <TextField {...params} sx={{ minWidth: 120 }} />
          )}
          disableClearable
        />

        <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />

        {/* Basic Formatting */}
        <ToggleButtonGroup size="small">
          <ToggleButton
            value="bold"
            selected={editor.isActive('bold')}
            onMouseDown={(e) => e.preventDefault()}
            onChange={() => {
              editor.chain().focus().toggleBold().run();
            }}
          >
            <FormatBoldIcon fontSize="small" />
          </ToggleButton>
          <ToggleButton
            value="italic"
            selected={editor.isActive('italic')}
            onMouseDown={(e) => e.preventDefault()}
            onChange={() => {
              editor.chain().focus().toggleItalic().run();
            }}
          >
            <FormatItalicIcon fontSize="small" />
          </ToggleButton>
          <ToggleButton
            value="underline"
            selected={editor.isActive('underline')}
            onMouseDown={(e) => e.preventDefault()}
            onChange={() => {
              editor.chain().focus().toggleUnderline().run();
            }}
          >
            <FormatUnderlinedIcon fontSize="small" />
          </ToggleButton>
        </ToggleButtonGroup>

        <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />

        {/* Text Alignment */}
        <ToggleButtonGroup size="small" exclusive>
          <ToggleButton
            value="left"
            selected={editor.isActive({ textAlign: 'left' })}
            onMouseDown={(e) => e.preventDefault()}
            onChange={() => editor.chain().focus().setTextAlign('left').run()}
          >
            <FormatAlignLeftIcon fontSize="small" />
          </ToggleButton>
          <ToggleButton
            value="center"
            selected={editor.isActive({ textAlign: 'center' })}
            onMouseDown={(e) => e.preventDefault()}
            onChange={() => editor.chain().focus().setTextAlign('center').run()}
          >
            <FormatAlignCenterIcon fontSize="small" />
          </ToggleButton>
          <ToggleButton
            value="right"
            selected={editor.isActive({ textAlign: 'right' })}
            onMouseDown={(e) => e.preventDefault()}
            onChange={() => editor.chain().focus().setTextAlign('right').run()}
          >
            <FormatAlignRightIcon fontSize="small" />
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {/* Row 2: Lists, Quotes, Media */}
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
        {/* Lists and Quotes */}
        <ButtonGroup size="small" variant="outlined">
          <Button
            variant={editor.isActive('bulletList') ? 'contained' : 'outlined'}
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => editor.chain().focus().toggleBulletList().run()}
            startIcon={<FormatListBulletedIcon />}
          >
            Bullets
          </Button>
          <Button
            variant={editor.isActive('orderedList') ? 'contained' : 'outlined'}
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => editor.chain().focus().toggleOrderedList().run()}
            startIcon={<FormatListNumberedIcon />}
          >
            Numbers
          </Button>
          <Button
            variant={editor.isActive('blockquote') ? 'contained' : 'outlined'}
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => editor.chain().focus().toggleBlockquote().run()}
            startIcon={<FormatQuoteIcon />}
          >
            Quote
          </Button>
        </ButtonGroup>

        <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />

        {/* Media and Links */}
        <ButtonGroup size="small" variant="outlined">
          <Tooltip title="Insert Symbol">
            <Button
              onClick={() => setShowSymbolPicker(!showSymbolPicker)}
              startIcon={<EmojiSymbolsIcon />}
              variant={showSymbolPicker ? 'contained' : 'outlined'}
            >
              Symbol
            </Button>
          </Tooltip>
          <Tooltip title="Insert Emoji">
            <Button
              onClick={() => setShowEmojiPicker(true)}
              startIcon={<EmojiEmotionsIcon />}
              variant={showEmojiPicker ? 'contained' : 'outlined'}
            >
              Emoji
            </Button>
          </Tooltip>
          <Button
            variant={editor.isActive('link') ? 'contained' : 'outlined'}
            onMouseDown={(e) => e.preventDefault()}
            onClick={toggleLink}
            startIcon={<LinkIcon />}
          >
            Link
          </Button>
        </ButtonGroup>

        <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />

        {/* Undo/Redo */}
        <ButtonGroup size="small" variant="outlined">
          <Tooltip title="Undo">
            <Button
              onClick={() => editor.chain().focus().undo().run()}
              disabled={!editor.can().undo()}
            >
              <UndoIcon fontSize="small" />
            </Button>
          </Tooltip>
          <Tooltip title="Redo">
            <Button
              onClick={() => editor.chain().focus().redo().run()}
              disabled={!editor.can().redo()}
            >
              <RedoIcon fontSize="small" />
            </Button>
          </Tooltip>
        </ButtonGroup>
      </Box>

      {/* Symbol Picker */}
      {showSymbolPicker && (
        <Box sx={{ 
          mt: 2, 
          p: 2, 
          border: 1, 
          borderColor: 'divider', 
          borderRadius: 1, 
          bgcolor: 'background.paper',
          maxWidth: 400 
        }}>
          <Typography variant="subtitle2" gutterBottom>
            Common Symbols
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {COMMON_SYMBOLS.map((symbol, index) => (
              <Button
                key={index}
                size="small"
                variant="outlined"
                onClick={() => insertSymbol(symbol)}
                sx={{ minWidth: 32, minHeight: 32 }}
              >
                {symbol}
              </Button>
            ))}
          </Box>
        </Box>
      )}

      {/* Link Dialog */}
      {showLinkDialog && (
        <Box sx={{ 
          mt: 2, 
          p: 2, 
          border: 1, 
          borderColor: 'divider', 
          borderRadius: 1, 
          bgcolor: 'background.paper' 
        }}>
          <Typography variant="subtitle2" gutterBottom>
            Add Link
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <TextField
              size="small"
              placeholder="Enter URL..."
              value={linkUrl}
              onChange={(e) => setLinkUrl(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && setLink()}
              sx={{ flexGrow: 1 }}
            />
            <Button variant="contained" onClick={setLink} disabled={!linkUrl}>
              Add
            </Button>
            <Button onClick={() => setShowLinkDialog(false)}>
              Cancel
            </Button>
          </Box>
        </Box>
      )}

      {/* Emoji Picker */}
      <EmojiPicker
        open={showEmojiPicker}
        onClose={() => setShowEmojiPicker(false)}
        onInsert={(emoji) => {
          console.log('🎭 Emoji inserted:', emoji);
        }}
        editor={editor}
      />
    </Box>
  );

  return (
    <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1 }}>
      <Toolbar />
      <Box 
        sx={{ 
          p: 3, 
          minHeight: 400,
          cursor: 'text',
          '& .ProseMirror': {
            outline: 'none !important',
            border: 'none',
            minHeight: 350,
            fontSize: '16px',
            lineHeight: 1.6,
            fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
            /* CSS Custom Properties for unified emoji scaling */
            '--base-font-size': '16px',
            '--emoji-scale-factor': '1',
            '&:focus': {
              outline: 'none !important',
            },
            '& p.is-editor-empty:first-child::before': {
              content: 'attr(data-placeholder)',
              float: 'left',
              color: 'rgba(0, 0, 0, 0.54)',
              pointerEvents: 'none',
              height: 0,
              fontStyle: 'italic',
            },
            /* Simple Emoji Scaling - Follows Text Size */
            '& .emoji-inline, & img[data-emoji-name], & img[alt*="emoji"]': {
              height: '1em !important',
              width: 'auto !important',
              display: 'inline !important',
              verticalAlign: 'middle !important',
              margin: '0 0.1em !important',
              objectFit: 'contain !important',
              border: 'none !important',
              background: 'transparent !important',
              fontSize: 'inherit !important',
              lineHeight: '1 !important',
              maxHeight: 'none !important',
              minHeight: 'none !important',
            },
            '& h1': {
              fontSize: '2rem',
              fontWeight: 700,
              margin: '1.2rem 0 0.6rem 0',
              lineHeight: 1.3,
              color: '#1976d2',
            },
            '& h2': {
              fontSize: '1.5rem',
              fontWeight: 600,
              margin: '1rem 0 0.5rem 0',
              lineHeight: 1.3,
              color: '#1976d2',
            },
            '& h3': {
              fontSize: '1.25rem',
              fontWeight: 600,
              margin: '1rem 0 0.5rem 0',
              lineHeight: 1.3,
              color: '#1976d2',
            },
            '& p': {
              margin: '0.8em 0',
              lineHeight: 1.7,
              '&:first-child': {
                marginTop: 0,
              },
              '&:last-child': {
                marginBottom: 0,
              },
            },
            '& ul, & ol': {
              paddingLeft: '1.5em',
              margin: '1em 0',
            },
            '& li': {
              margin: '0.3em 0',
              lineHeight: 1.6,
            },
            '& blockquote': {
              borderLeft: '4px solid #1976d2',
              marginLeft: 0,
              marginRight: 0,
              paddingLeft: '1.2em',
              fontStyle: 'italic',
              backgroundColor: 'rgba(25, 118, 210, 0.04)',
              padding: '0.8em 1.2em',
              margin: '1.5em 0',
              borderRadius: '0 4px 4px 0',
              color: 'rgba(0, 0, 0, 0.7)',
            },
            '& .handbook-image': {
              maxWidth: '100%',
              height: 'auto',
              borderRadius: '4px',
              border: '1px solid #e0e0e0',
              cursor: 'pointer',
              transition: 'border-color 0.2s ease',
              '&:hover': {
                borderColor: '#1976d2',
              },
              '&.ProseMirror-selectednode': {
                borderColor: '#1976d2',
                borderWidth: '2px',
                outline: 'none',
                position: 'relative',
                '&::after': {
                  content: '""',
                  position: 'absolute',
                  top: '-4px',
                  right: '-4px',
                  bottom: '-4px',
                  left: '-4px',
                  border: '2px solid #1976d2',
                  borderRadius: '4px',
                  pointerEvents: 'none',
                },
              },
            },
            '& img.ProseMirror-selectednode': {
              borderColor: '#1976d2',
              borderWidth: '2px',
              outline: 'none',
            },
            '& .handbook-link': {
              color: '#1976d2',
              textDecoration: 'underline',
              '&:hover': {
                color: '#1565c0',
                textDecoration: 'underline',
              },
            },
            '& strong': {
              fontWeight: 700,
            },
            '& em': {
              fontStyle: 'italic',
            },
            '& u': {
              textDecoration: 'underline',
            },
          }
        }}
      >
        <EditorContent editor={editor} />
      </Box>
    </Box>
  );
};

export default EnhancedRichTextEditor;